import { useMemo, useEffect } from 'react';
import { OperationContext } from '@urql/core';
import { SWMessageChannel } from '@studio/entitlements';
import { GET_ACCOUNT_ENTITLEMENTS } from 'studio5/hooks/queries';
import {
  GetAccountEntitlementQuery,
  GetAccountEntitlementQueryVariables,
} from 'studio5/hooks/__generated__/queries.types';
import { useGraphQLQuery } from 'studio5/contexts';
import { EntitlementName } from 'studio5/types/entitlements.types';

const channel = new SWMessageChannel();

export function useEntitlement(
  entitlementName: EntitlementName,
  connectedTypeNames?: string[]
) {
  const operationContext: Partial<OperationContext> = useMemo(
    () => ({
      additionalTypenames: connectedTypeNames,
    }),
    [connectedTypeNames]
  );

  // eslint-disable-next-line @appcues/use-context-hooks-in-provider
  const [result, refetch] = useGraphQLQuery<
    GetAccountEntitlementQuery,
    GetAccountEntitlementQueryVariables
  >({
    query: GET_ACCOUNT_ENTITLEMENTS,
    context: operationContext,
    pause: false,
    requestPolicy: 'network-only',
  });

  const entitlement = useMemo(() => {
    return result.data?.accountEntitlements?.find(
      ({ name }) => entitlementName === name
    );
  }, [entitlementName, result.data?.accountEntitlements]);

  useEffect(() => {
    const unsubscribe = channel.listenForMessage(() => {
      setTimeout(() => {
        refetch({ requestPolicy: 'network-only' });
      }, 500);
    });

    return () => {
      unsubscribe();
    };
  }, [refetch]);

  const isEntitled =
    entitlement && entitlement?.allowedUnits > entitlement?.usedUnits;

  return [isEntitled, { entitlement, result, refetch }] as const;
}
